import { Timestamp } from '@firebase/firestore';

import { Step } from '../models/Recipe';

type TimestampLike = Timestamp | { seconds: number; nanoseconds: number } | { _seconds: number; _nanoseconds: number }; // sometimes seen from admin SDK/serialized

function hasToDate(x: unknown): x is { toDate: () => Date } {
  return !!x && typeof x === 'object' && typeof (x as any).toDate === 'function';
}

function isSecondsNanos(x: unknown): x is { seconds: number; nanoseconds: number } {
  return (
    !!x && typeof x === 'object' && typeof (x as any).seconds === 'number' && typeof (x as any).nanoseconds === 'number'
  );
}

function isUnderscoreSecondsNanos(x: unknown): x is { _seconds: number; _nanoseconds: number } {
  return (
    !!x &&
    typeof x === 'object' &&
    typeof (x as any)._seconds === 'number' &&
    typeof (x as any)._nanoseconds === 'number'
  );
}

export function toDate(input?: Date | TimestampLike | number | string): Date {
  if (input == null) return new Date(0);

  // Real Timestamp (any bundle) – prefer feature check over instanceof
  if (hasToDate(input)) return input.toDate();

  // Duck-typed {seconds, nanoseconds}
  if (isSecondsNanos(input)) {
    return new Date(input.seconds * 1000 + input.nanoseconds / 1e6);
  }

  // Duck-typed {_seconds, _nanoseconds}
  if (isUnderscoreSecondsNanos(input)) {
    return new Date(input._seconds * 1000 + input._nanoseconds / 1e6);
  }

  // Native Date
  if (input instanceof Date) return input;

  // Millis vs seconds number
  if (typeof input === 'number') {
    const asMillis = Math.abs(input) < 1e12 ? input * 1000 : input; // < ~Sat Nov 20 33658
    return new Date(asMillis);
  }

  // ISO string or anything Date can parse
  if (typeof input === 'string') {
    const t = Date.parse(input);
    return Number.isNaN(t) ? new Date(0) : new Date(t);
  }

  // Last resort: let Date try (may be implementation-specific)
  return new Date(input as any);
}

export function addDays(date: Date, daysToAdd: number): Date {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() + daysToAdd);
  return newDate;
}

export function addDaysBySteps(date: Date, steps: Step[]): Date {
  let newDate = new Date(date);

  steps?.forEach((step) => {
    // if endDate is set, return it, use diff between endDate and startDate insted of step.duration
    if (step.end) {
      newDate = toDate(step.end);
      return;
    }
    newDate = addDays(newDate, step.duration);
  });

  return newDate;
}
